import React from "react";
import { ArrowRight, Star, Sparkles } from "lucide-react";
import { HERO_IMAGE, CLINIC } from "@/lib/content";

export default function Hero({ onBook }) {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden"
        >
            <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#8fa998]/15 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full bg-[#d4af37]/10 blur-3xl" />

            <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7 relative z-10 gdc-fade-up">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-[#e8e4db] px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[#4a5250]">
                        <Sparkles
                            size={12}
                            className="text-[#d4af37]"
                            strokeWidth={2}
                        />
                        Udaipur’s Signature Smile Studio
                    </div>

                    <h1 className="font-serif-display mt-6 text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#1a1d1c]">
                        Your smile,
                        <br />
                        <span className="italic text-[#114b5f]">
                            our masterpiece.
                        </span>
                    </h1>

                    <p className="mt-6 max-w-xl text-base md:text-lg text-[#4a5250] leading-relaxed">
                        Galaxy Dental Clinic pairs master craftsmanship with
                        modern digital dentistry — implants, aligners, veneers
                        and painless root canals delivered in a calm,
                        five-star studio in the heart of Udaipur.
                    </p>

                    <div className="mt-9 flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={onBook}
                            data-testid="hero-book-appointment-button"
                            className="rounded-full bg-[#114b5f] text-[#faf9f6] px-8 py-4 text-sm font-medium hover:bg-[#0d3a4a] transition-all hover:shadow-lg flex items-center gap-2"
                        >
                            Book an Appointment
                            <ArrowRight size={16} strokeWidth={1.75} />
                        </button>
                        <a
                            href="#services"
                            data-testid="hero-explore-services"
                            className="rounded-full border border-[#114b5f] text-[#114b5f] px-8 py-4 text-sm font-medium hover:bg-[#114b5f] hover:text-[#faf9f6] transition-all"
                        >
                            Explore Services
                        </a>
                    </div>

                    <div className="mt-10 flex items-center gap-6">
                        <div className="flex items-center gap-1">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <Star
                                    key={i}
                                    size={16}
                                    strokeWidth={1.5}
                                    className="fill-[#d4af37] text-[#d4af37]"
                                />
                            ))}
                        </div>
                        <div className="text-sm text-[#4a5250]">
                            <span className="font-medium text-[#1a1d1c]">
                                4.8 / 5
                            </span>{" "}
                            · 530+ Google Reviews
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-5 relative">
                    <div className="relative gdc-float">
                        <div className="absolute -inset-4 bg-[#8fa998]/20 rounded-t-full blur-2xl" />
                        <img
                            src={HERO_IMAGE}
                            alt="Perfect smile"
                            className="relative w-full aspect-[3/4] object-cover rounded-t-full ring-1 ring-black/5 shadow-2xl"
                        />
                    </div>
                    <div className="absolute -bottom-6 -left-4 md:-left-10 bg-white rounded-2xl shadow-xl border border-[#e8e4db] p-5 max-w-[240px]">
                        <div className="text-3xl font-serif-display text-[#114b5f]">
                            1,000+
                        </div>
                        <div className="text-xs text-[#4a5250] mt-1">
                            Successful implants placed by Dr. Swapnil Tamrakar
                        </div>
                    </div>
                    <div className="absolute -top-4 -right-2 md:-right-6 bg-[#114b5f] text-[#faf9f6] rounded-2xl shadow-xl p-4 max-w-[180px] hidden md:block">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#d4af37]">
                            Book today
                        </div>
                        <div className="text-sm mt-1">
                            Same-day emergency slots available
                        </div>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-6 md:px-12 mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6">
                {CLINIC.stats.map((s) => (
                    <div
                        key={s.label}
                        data-testid={`hero-stat-${s.label
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        className="bg-[#f3f0ea] rounded-2xl p-6"
                    >
                        <div className="font-serif-display text-3xl md:text-4xl text-[#114b5f]">
                            {s.value}
                        </div>
                        <div className="mt-1 text-xs uppercase tracking-[0.2em] text-[#7a8582]">
                            {s.label}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}
